/**
 * App-level watcher for in-flight bulk-upload jobs.
 *
 * Mount once near the app root. On mount it hydrates from BOTH job
 * endpoints (so a tab reload doesn't orphan an in-flight job):
 *
 *   - GET /api/v1/designs/upload-jobs   (design processing jobs)
 *   - GET /api/v1/mockups/upload-jobs   (Etsy listing jobs)
 *
 * Polls every ~10s while any active jobs exist, sleeps to a 60s
 * heartbeat when none do.
 *
 * Each job carries a ``kind`` discriminator (``'design'`` or
 * ``'etsy'``) so terminal toasts can use kind-appropriate copy. Design
 * jobs that chain to an Etsy job (``etsy_job_id`` populated) suppress
 * the design-terminal toast for the success path — the chained Etsy
 * job's terminal toast is the more meaningful user-facing signal.
 */
import { useEffect, useRef } from 'react';
import { useApi } from './useApi';
import { useAuth } from './useAuth';
import { useNotifications } from '../components/NotificationSystem';
import API_ENDPOINTS from '../constants/apiEndpoints';
import useUploadJobsStore, { isTerminalStatus } from '../stores/uploadJobsStore';
import useJobProgressStore from '../stores/jobProgressStore';

const POLL_INTERVAL_MS = 10000;
// Faster cadence right after the page loads or a job is freshly enqueued,
// so the user sees "running" → "succeeded" quickly during the most likely
// observation window. Backs off to POLL_INTERVAL_MS after ~30s of idleness.
const FAST_POLL_INTERVAL_MS = 3000;
const FAST_POLL_BURST_MS = 30000;

const useUploadJobsWatcher = () => {
  const api = useApi();
  const { isAuthenticated } = useAuth();
  const { addNotification } = useNotifications();
  const hydrateJobs = useUploadJobsStore(s => s.hydrateJobs);
  const markNotified = useUploadJobsStore(s => s.markNotified);
  const intervalRef = useRef(null);
  // Track first-mount-time so we can run the fast cadence for a bounded window.
  const fastUntilRef = useRef(0);

  useEffect(() => {
    if (!isAuthenticated) {
      if (intervalRef.current) {
        clearTimeout(intervalRef.current);
        intervalRef.current = null;
      }
      return undefined;
    }

    let cancelled = false;
    fastUntilRef.current = Date.now() + FAST_POLL_BURST_MS;

    const fireTerminalToast = job => {
      // Design-job terminal that already chained to an Etsy job: stay
      // quiet on the success path. The Etsy job's terminal will be the
      // toast the seller cares about ("X listings created"). We still
      // toast on design-job failure / duplicate-only so the user isn't
      // left wondering when no Etsy job follows.
      if (job.kind === 'design' && job.status === 'succeeded' && job.etsy_job_id) {
        return;
      }

      const ok = job.succeeded_count ?? 0;
      const fail = job.failed_count ?? 0;
      const total = job.total_designs ?? job.total_files ?? 0;

      if (job.kind === 'design') {
        switch (job.status) {
          case 'succeeded':
            addNotification('success', `Design upload complete — ${ok} design${ok === 1 ? '' : 's'} processed.`);
            break;
          case 'partial':
            // Prefer the backend message when present (e.g. designs saved but
            // the Etsy listing step couldn't start) — the generic counts line
            // is the fallback for design-processing partials.
            addNotification(
              'warning',
              job.message || `Design upload finished with errors: ${ok}/${total} succeeded, ${fail} failed.`
            );
            break;
          case 'failed':
            addNotification('error', job.message || 'Design upload failed. Check the upload tab for details.');
            break;
          case 'duplicate':
            addNotification(
              'info',
              job.message || 'All uploaded files matched existing designs in your library — nothing new to list.'
            );
            break;
          case 'deferred':
            // Defensive: design jobs don't normally hit Etsy rate limits, but
            // if the backend ever surfaces 'deferred' on a design job we treat
            // it the same as on Etsy jobs — informational, never an error.
            addNotification(
              'info',
              job.message ||
                'Upload paused — Etsy is rate-limiting requests. Your designs are saved; you can retry shortly.'
            );
            break;
          default:
            break;
        }
        return;
      }

      if (job.kind === 'import') {
        // CraftFlow Commerce bulk import-from-designs (async job).
        switch (job.status) {
          case 'succeeded':
            addNotification('success', `Product import complete — ${ok} product${ok === 1 ? '' : 's'} created.`);
            break;
          case 'partial':
            addNotification(
              'warning',
              job.message || `Product import finished with errors: ${ok}/${total} created, ${fail} failed.`
            );
            break;
          case 'failed':
            addNotification('error', job.message || 'Product import failed. Check the products page for details.');
            break;
          case 'duplicate':
            addNotification(
              'info',
              job.message || 'All selected designs were already imported — nothing new to create.'
            );
            break;
          default:
            break;
        }
        return;
      }

      if (job.kind === 'shopify') {
        // Async Shopify bulk product create.
        switch (job.status) {
          case 'succeeded':
            addNotification('success', `Shopify upload complete — ${ok} product${ok === 1 ? '' : 's'} created.`);
            break;
          case 'partial':
            addNotification(
              'warning',
              job.message || `Shopify upload finished with errors: ${ok}/${total} created, ${fail} failed.`
            );
            break;
          case 'failed':
            addNotification('error', job.message || 'Shopify upload failed. Check the products page for details.');
            break;
          case 'duplicate':
            addNotification('info', job.message || 'All designs matched existing products — nothing new created.');
            break;
          default:
            break;
        }
        return;
      }

      if (job.kind === 'craftflow-product') {
        // Async CraftFlow bulk-template product upload.
        switch (job.status) {
          case 'succeeded':
            addNotification('success', `Product upload complete — ${ok} product${ok === 1 ? '' : 's'} created.`);
            break;
          case 'partial':
            addNotification(
              'warning',
              job.message || `Product upload finished with errors: ${ok}/${total} created, ${fail} failed.`
            );
            break;
          case 'failed':
            addNotification('error', job.message || 'Product upload failed. Check the products page for details.');
            break;
          case 'duplicate':
            addNotification('info', job.message || 'All designs matched existing products — nothing new created.');
            break;
          default:
            break;
        }
        return;
      }

      // kind === 'etsy'
      switch (job.status) {
        case 'succeeded':
          addNotification('success', `Etsy upload complete — ${ok} listing${ok === 1 ? '' : 's'} created.`);
          break;
        case 'partial':
          addNotification('warning', `Etsy upload finished with errors: ${ok}/${total} succeeded, ${fail} failed.`);
          break;
        case 'failed':
          addNotification('error', job.message || 'Etsy upload failed. Check the upload tab for details.');
          break;
        case 'duplicate':
          // Quietly skip — same as before.
          break;
        case 'deferred':
          // Etsy rate-limited the worker. Nothing was lost — designs/mockups
          // are preserved and the user can retry. Show as info, not an error,
          // so a transient throttle doesn't look like a real failure.
          addNotification(
            'info',
            job.message ||
              'Upload paused — Etsy is rate-limiting requests. Your designs are saved; you can retry shortly.'
          );
          break;
        default:
          break;
      }
    };

    const poll = async () => {
      if (cancelled) return;
      try {
        // Parallel fetch of both job kinds. Promise.allSettled so one
        // endpoint failing doesn't stall the other.
        const [designResult, etsyResult, importResult, shopifyResult, productResult] = await Promise.allSettled([
          api.get(API_ENDPOINTS.MOCKUPS.DESIGN_UPLOAD_JOBS_LIST),
          api.get(API_ENDPOINTS.MOCKUPS.UPLOAD_JOBS_LIST),
          api.get(API_ENDPOINTS.ECOMMERCE.IMPORT_JOBS),
          api.get(API_ENDPOINTS.SHOPIFY.UPLOAD_JOBS_LIST),
          api.get(API_ENDPOINTS.ECOMMERCE.PRODUCT_JOBS),
        ]);
        if (cancelled) return;

        const designJobs = designResult.status === 'fulfilled' ? designResult.value || [] : [];
        const etsyJobs = etsyResult.status === 'fulfilled' ? etsyResult.value || [] : [];
        const importJobs = importResult.status === 'fulfilled' ? importResult.value || [] : [];
        const shopifyJobs = shopifyResult.status === 'fulfilled' ? shopifyResult.value || [] : [];
        const productJobs = productResult.status === 'fulfilled' ? productResult.value || [] : [];
        const merged = [
          ...designJobs.map(j => ({ ...j, kind: 'design' })),
          ...etsyJobs.map(j => ({ ...j, kind: 'etsy' })),
          ...importJobs.map(j => ({ ...j, kind: 'import' })),
          ...shopifyJobs.map(j => ({ ...j, kind: 'shopify' })),
          ...productJobs.map(j => ({ ...j, kind: 'craftflow-product' })),
        ];

        // Snapshot prior state BEFORE hydration so we can diff for new
        // terminal transitions.
        const priorJobs = useUploadJobsStore.getState().jobs;
        hydrateJobs(merged);

        for (const job of merged) {
          if (!isTerminalStatus(job.status)) continue;
          const prior = priorJobs[job.id];
          if (prior?._notified) continue;
          fireTerminalToast(job);
          markNotified(job.id);
        }

        // Surface in-flight uploads in the global job-progress UI (as toasts —
        // ProductUploadModal owns its own in-modal progress, so we don't steal
        // focus). Drop them on terminal so the watcher's completion toast above
        // stays the single terminal signal (and the design→etsy handoff is
        // automatic: the terminal design job leaves, the active etsy job stays).
        const { updateJob: feedJob, removeJob: dropJob } = useJobProgressStore.getState();
        for (const job of merged) {
          if (isTerminalStatus(job.status)) {
            dropJob(job.id);
          } else {
            feedJob(job.id, job, {
              kind: 'upload',
              label: 'Bulk upload',
              accent: 'indigo',
              meta: { phaseKind: job.kind === 'etsy' ? 'etsy' : 'design' },
            });
          }
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn('useUploadJobsWatcher: poll failed', err);
      } finally {
        if (cancelled) return;
        const hasActive = useUploadJobsStore.getState().hasActiveJobs();
        let nextDelay;
        if (!hasActive) {
          nextDelay = 60000;
        } else if (Date.now() < fastUntilRef.current) {
          nextDelay = FAST_POLL_INTERVAL_MS;
        } else {
          nextDelay = POLL_INTERVAL_MS;
        }
        intervalRef.current = setTimeout(poll, nextDelay);
      }
    };

    // Kick the first poll immediately on mount.
    poll();

    return () => {
      cancelled = true;
      if (intervalRef.current) {
        clearTimeout(intervalRef.current);
        intervalRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  // Expose a way for the modal to reset the fast-poll window when a new
  // job is submitted so the watcher catches the running transition quickly.
  const triggerFastPoll = () => {
    fastUntilRef.current = Date.now() + FAST_POLL_BURST_MS;
  };

  return { triggerFastPoll };
};

export default useUploadJobsWatcher;
